"use client";
import { CryptoCurrencyID } from "@/shared/constants";
import {
  calculateTotal,
  formatDecimals,
  toHighDenom,
  toLowDenom,
} from "@/shared/currencies";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import useCryptoInUSD from "@/shared/hooks/useCryptoInUSD";
import { useMemo } from "react";
import useNftExchangeFees from "@/web3/contracts/PaymentManager/actions/useNftExchangeFees";
import { BaseSignedOrder } from "@/api/orders/types";

export interface SellerGet {
  cryptoValue: number | string | undefined;
  cryptoName: string | undefined;
  usdValue: string | undefined;
}

export function useAuctionSellerGet(
  nftId: string,
  price: number,
  paymentMethodAddress: string,
) {
  const nftFeeData = useNftExchangeFees(nftId);

  return useSellerGet(
    price,
    paymentMethodAddress,
    nftFeeData?.rawMakerFee,
    nftFeeData?.rawRoyaltiesFee,
    nftFeeData?.rawCollectiveFee,
  );
}
export function useOfferSellerGet(order: BaseSignedOrder) {
  return useSellerGet(
    toHighDenom(order.price)!,
    order.paymentErc20TokenAddress,
    order.makerFee,
    order.royaltiesFee,
    order.collectiveFee,
  );
}

export default function useSellerGet(
  price: number,
  paymentMethodAddress: string,
  makerFee = 0,
  royaltiesFee = 0,
  collectiveFee = 0,
) {
  const paymentMethod = usePaymentCurrency(paymentMethodAddress);

  const { cryptoInUSD: priceInUSD } = useCryptoInUSD(
    price,
    paymentMethod?.id as CryptoCurrencyID,
  );

  return useMemo<SellerGet>(() => {
    if (!price || !priceInUSD) {
      return {
        cryptoValue: 0,
        cryptoName: paymentMethod?.label,
        usdValue: "0",
      };
    }

    const totalSellerGet = calculateTotal(
      +toLowDenom(price, paymentMethod?.decimals)!,
      BigInt(makerFee),
      BigInt(royaltiesFee),
      BigInt(collectiveFee),
    );

    const inHighDenom = toHighDenom(totalSellerGet, paymentMethod?.decimals);

    return {
      cryptoValue: formatDecimals(inHighDenom),
      cryptoName: paymentMethod?.label,
      usdValue: priceInUSD.toString(),
    };
  }, [
    paymentMethod?.decimals,
    paymentMethod?.label,
    price,
    priceInUSD,
    makerFee,
    royaltiesFee,
    collectiveFee,
  ]);
}
